import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { 
  BranchEntity, 
  ApiResponse, 
  CountryLookup,
  CityLookup,
} from '@/api/types'

/**
 * Branch management service
 * Handles all branch-related API operations with proper typing
 */
export class BranchService {
  /**
   * Get all branches
   */
  async getAllBranches(): Promise<BranchEntity[]> {
    const url = endpoints.branches.getAll
    const response = await apiClient.get<ApiResponse<BranchEntity[]> | BranchEntity[]>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && Array.isArray(response.data.data) && 'success' in response.data) {
      return (response.data as ApiResponse<BranchEntity[]>).data
    }
    // API returns direct array
    return Array.isArray(response.data) ? response.data : []
  }

  /**
   * Get single branch by ID
   */
  async getBranchById(id: number): Promise<BranchEntity> {
    const url = endpoints.branches.getById(id)
    const response = await apiClient.get<ApiResponse<BranchEntity> | BranchEntity>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data) {
      return (response.data as ApiResponse<BranchEntity>).data
    }
    return response.data as BranchEntity
  }

  /**
   * Create new branch (multipart/form-data)
   */
  async saveBranch(formData: FormData): Promise<ApiResponse<BranchEntity>> {
    const response = await apiClient.post<ApiResponse<BranchEntity>>(
      endpoints.branches.save,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  /**
   * Update existing branch (multipart/form-data)
   */
  async updateBranch(formData: FormData): Promise<ApiResponse<BranchEntity>> {
    const response = await apiClient.put<ApiResponse<BranchEntity>>(
      endpoints.branches.update,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  /**
   * Delete branch
   */
  async deleteBranch(id: number): Promise<ApiResponse<void>> {
    const url = endpoints.branches.delete(id)
    const response = await apiClient.delete<ApiResponse<void>>(url)
    return response.data
  }

  /**
   * Get country lookups
   */
  async getCountryLookups(): Promise<CountryLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.branches.getCountries)
      console.log('Country lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || item.nameEn || '',
          lookupNameAr: item.lookupNameAr || item.nameAr || '',
        })).filter((item: CountryLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching country lookups:', error)
      return []
    }
  }

  /**
   * Get city lookups
   */
  async getCityLookups(countryId?: number): Promise<CityLookup[]> {
    try {
      const url = endpoints.branches.getCities(countryId)
      const response = await apiClient.get<any>(url)
      console.log('City lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || item.nameEn || '',
          lookupNameAr: item.lookupNameAr || item.nameAr || '',
        })).filter((item: CityLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching city lookups:', error)
      return []
    }
  }
}

export const branchService = new BranchService()

